/**
 * Cash Expenses Ledger (Feb 2026)
 * =================================================================
 * Weekly / period cash-drawer view for contracting operators.
 * Lists every expense whose ``payment_source='cash'`` within the
 * requested date range, with totals grouped by expense type, project,
 * and cash account.
 *
 * Purpose: give operators a clean cash outflow view (11150 · Cash
 * Expenses by default) without the custody-fund noise of Petty Cash.
 *
 * Route:  /cash-expenses-ledger
 * Backend: GET /api/reports/cash-expenses-ledger
 */
import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Loader2, RefreshCw, DollarSign, Wallet, Building, FileDown, Scale } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Default range = current month (first day → today).
const _today = new Date();
const _isoDate = (d) => d.toISOString().slice(0, 10);
const _firstOfMonth = new Date(_today.getFullYear(), _today.getMonth(), 1);

export default function CashExpensesLedger() {
  const [startDate, setStartDate] = useState(_isoDate(_firstOfMonth));
  const [endDate, setEndDate]     = useState(_isoDate(_today));
  const [cashAccountId, setCashAccountId] = useState('');
  const [cashAccounts, setCashAccounts]   = useState([]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [settleStatus, setSettleStatus] = useState(null);
  const [settleOpen, setSettleOpen]     = useState(false);
  const [settleAmount, setSettleAmount] = useState('');
  const [settleDate, setSettleDate]     = useState(_isoDate(_today));
  const [settleMemo, setSettleMemo]     = useState('');
  const [settling, setSettling]         = useState(false);

  const fetchSettleStatus = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/owner-settlement/status');
      setSettleStatus(r.data);
      setSettleAmount(String(r.data?.suggested_amount || ''));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchSettleStatus(); }, [fetchSettleStatus]);

  const doSettle = async () => {
    const amt = parseFloat(settleAmount);
    if (!amt || amt <= 0) { toast.error('أدخل مبلغاً صحيحاً'); return; }
    setSettling(true);
    try {
      const r = await axiosInstance.post('/owner-settlement/settle', {
        amount: amt, settlement_date: settleDate, memo: settleMemo,
      });
      toast.success(`تمت التسوية — قيد ${r.data.journal_no} · الرصيد الجديد ${fmt(r.data.new_balance)}`);
      setSettleOpen(false);
      setSettleMemo('');
      window.dispatchEvent(new Event('audit-refresh'));
      await fetchSettleStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشلت التسوية');
    } finally {
      setSettling(false);
    }
  };

  // Load available cash accounts (is_cash_account=true) so the operator
  // can drill into a specific drawer (e.g. 11150 vs 11010).
  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get('/accounts');
        setCashAccounts(
          (r.data || []).filter(a =>
            a.account_type === 'assets'
            && a.is_cash_account === true
            && a.status !== 'inactive'
          )
        );
      } catch {
        /* silent: filter is optional */
      }
    })();
  }, []);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate)   params.append('end_date',   endDate);
      if (cashAccountId) params.append('cash_account_id', cashAccountId);
      const r = await axiosInstance.get(`/reports/cash-expenses-ledger?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, cashAccountId]);

  useEffect(() => {
    // Auto-load on mount. Dependencies intentionally omitted — filter
    // changes must be explicit (Apply button) to avoid rapid re-queries
    // while the operator types.
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = () => {
    if (!data?.rows?.length) {
      toast.info('لا توجد بيانات للتصدير');
      return;
    }
    const header = ['Date','Expense #','Vendor','Type','Project','Amount','VAT','Total (SAR)','Cash Account'];
    const csvRows = [header.join(',')];
    data.rows.forEach(e => {
      const cells = [
        e.expense_date || '',
        e.expense_no || '',
        (e.expense_from || '').replaceAll(',', ' '),
        (e.expense_type || '').replaceAll(',', ' '),
        (e.project_name || 'General').replaceAll(',', ' '),
        e.amount || 0,
        e.vat || 0,
        e.total_with_vat || 0,
        (e.cash_account_name || '').replaceAll(',', ' '),
      ];
      csvRows.push(cells.join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cash-expenses-${startDate}_to_${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const totals = data?.totals || { count: 0, amount: 0, vat: 0, total: 0 };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="cash-expenses-ledger-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-emerald-600" />
            دفتر المصروفات النقدية · Cash Expenses Ledger
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            كل التدفقات النقدية المباشرة (payment_source=cash) خلال الفترة، بدون خلط مع Petty Cash.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="cash-ledger-refresh-btn"
            onClick={run}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            data-testid="cash-ledger-export-btn"
            onClick={downloadCsv}
            disabled={!data?.rows?.length}
            className="btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <FileDown className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-600 mb-1 block">From</label>
            <input
              type="date"
              data-testid="cash-ledger-start-date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600 mb-1 block">To</label>
            <input
              type="date"
              data-testid="cash-ledger-end-date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-600 mb-1 block">Cash Account (optional)</label>
            <select
              data-testid="cash-ledger-account-filter"
              value={cashAccountId}
              onChange={e => setCashAccountId(e.target.value)}
              className="form-select w-full"
            >
              <option value="">— جميع الحسابات النقدية —</option>
              {cashAccounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.account_number} · {a.account_name}
                  {a.name_arabic ? ` — ${a.name_arabic}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <button
            data-testid="cash-ledger-apply-btn"
            onClick={run}
            disabled={loading}
            className="btn-primary"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Owner settlement card — funds the cash wallet from the owner */}
      {settleStatus?.configured && (
        <div
          data-testid="owner-settlement-card"
          className={`rounded-lg border p-4 ${settleStatus.gl_balance < -1
            ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 font-semibold text-gray-900">
                <Scale className="w-5 h-5 text-gray-700" />
                رصيد {settleStatus.cash_account?.account_number} · مصروفات نقدية (من دفتر الأستاذ):
                <span
                  data-testid="cash-expenses-gl-balance"
                  className={settleStatus.gl_balance < -1 ? 'text-red-600' : 'text-emerald-700'}
                >
                  {fmt(settleStatus.gl_balance)} SAR
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1">
                {settleStatus.gl_balance < -1
                  ? 'الرصيد سالب لأن المصروفات النقدية تُصرف دون تسجيل مصدر تمويلها. سوِّ الرصيد على حساب "جاري المالك" ليصبح صفراً ويُسجَّل التزام الشركة تجاه المالك.'
                  : 'الرصيد سليم — التسوية التلقائية مفعّلة: أي مصروف نقدي جديد يُحوَّل فوراً إلى جاري المالك دون أي إجراء يدوي.'}
                {settleStatus.owner_account && (
                  <span className="block mt-1">
                    جاري المالك ({settleStatus.owner_account.account_number}): رصيد {fmt(Math.abs(settleStatus.owner_account.gl_balance))} SAR دائن
                  </span>
                )}
              </p>
            </div>
            {settleStatus.gl_balance < -1 && !settleOpen && (
              <button
                data-testid="owner-settle-open-btn"
                onClick={() => setSettleOpen(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Scale className="w-4 h-4" />
                تسوية جاري المالك
              </button>
            )}
          </div>
          {settleOpen && (
            <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">المبلغ (SAR)</label>
                  <input
                    type="number"
                    data-testid="owner-settle-amount"
                    value={settleAmount}
                    onChange={e => setSettleAmount(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">تاريخ القيد</label>
                  <input
                    type="date"
                    data-testid="owner-settle-date"
                    value={settleDate}
                    onChange={e => setSettleDate(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">ملاحظة (اختياري)</label>
                  <input
                    type="text"
                    data-testid="owner-settle-memo"
                    value={settleMemo}
                    onChange={e => setSettleMemo(e.target.value)}
                    className="form-input w-full"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                سيُنشأ قيد: مدين {settleStatus.cash_account?.account_number} مصروفات نقدية / دائن جاري المالك — ويصبح رصيد الصندوق صفراً.
              </p>
              <div className="flex justify-end gap-2 mt-3">
                <button
                  data-testid="owner-settle-cancel-btn"
                  onClick={() => setSettleOpen(false)}
                  className="btn-secondary"
                >إلغاء</button>
                <button
                  data-testid="owner-settle-confirm-btn"
                  onClick={doSettle}
                  disabled={settling}
                  className="btn-primary flex items-center gap-2"
                >
                  {settling && <Loader2 className="w-4 h-4 animate-spin" />}
                  تأكيد التسوية
                </button>
              </div>
            </div>
          )}
          {settleStatus.settlements?.length > 0 && (
            <div className="mt-3 text-xs text-gray-600" data-testid="owner-settlements-history">
              آخر التسويات: {settleStatus.settlements.map(s => `${s.journal_no} (${fmt(s.amount)})`).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard
          icon={<Wallet className="w-5 h-5 text-emerald-600" />}
          label="عدد الحركات"
          value={totals.count}
          testid="cash-kpi-count"
        />
        <KpiCard
          icon={<DollarSign className="w-5 h-5 text-blue-600" />}
          label="صافي المبلغ (قبل الضريبة)"
          value={`${fmt(totals.amount)} SAR`}
          testid="cash-kpi-amount"
        />
        <KpiCard
          icon={<Building className="w-5 h-5 text-orange-600" />}
          label="ضريبة القيمة المضافة"
          value={`${fmt(totals.vat)} SAR`}
          testid="cash-kpi-vat"
        />
        <KpiCard
          icon={<DollarSign className="w-5 h-5 text-rose-600" />}
          label="إجمالي التدفق النقدي"
          value={`${fmt(totals.total)} SAR`}
          highlight
          testid="cash-kpi-total"
        />
      </div>

      {/* Group summaries */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GroupCard title="حسب النوع" data={data?.by_type} testid="cash-by-type" />
        <GroupCard title="حسب المشروع" data={data?.by_project} testid="cash-by-project" />
        <GroupCard title="حسب الحساب النقدي" data={data?.by_account} testid="cash-by-account" />
      </div>

      {/* Rows table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-gray-800">تفاصيل الحركات — {data?.rows?.length || 0} حركة</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cash-ledger-table">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="text-start px-3 py-2">التاريخ</th>
                <th className="text-start px-3 py-2">رقم</th>
                <th className="text-start px-3 py-2">المورد / الجهة</th>
                <th className="text-start px-3 py-2">النوع</th>
                <th className="text-start px-3 py-2">المشروع</th>
                <th className="text-end px-3 py-2">المبلغ</th>
                <th className="text-end px-3 py-2">ضريبة</th>
                <th className="text-end px-3 py-2">الإجمالي</th>
                <th className="text-start px-3 py-2">الحساب النقدي</th>
              </tr>
            </thead>
            <tbody>
              {(!data?.rows || data.rows.length === 0) && (
                <tr>
                  <td colSpan={9} className="text-center text-gray-500 py-8" data-testid="cash-ledger-empty">
                    {loading ? 'جارِ التحميل…' : 'لا توجد حركات نقدية في هذه الفترة.'}
                  </td>
                </tr>
              )}
              {data?.rows?.map(e => (
                <tr key={e.id} className="border-t border-gray-100 hover:bg-emerald-50/40" data-testid={`cash-row-${e.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{e.expense_date || '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs text-purple-700">{e.expense_no || '—'}</td>
                  <td className="px-3 py-2">{e.expense_from || '—'}</td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100 text-xs">
                      {e.expense_type || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2">{e.project_name || <span className="text-gray-400">General</span>}</td>
                  <td className="px-3 py-2 text-end">{fmt(e.amount)}</td>
                  <td className="px-3 py-2 text-end text-orange-600">{fmt(e.vat)}</td>
                  <td className="px-3 py-2 text-end font-semibold text-rose-700">{fmt(e.total_with_vat)}</td>
                  <td className="px-3 py-2 text-emerald-700">{e.cash_account_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, highlight = false, testid }) {
  return (
    <div
      className={`p-4 rounded-lg border ${highlight ? 'border-rose-200 bg-rose-50/50' : 'border-gray-200 bg-white'}`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
        {icon}
        {label}
      </div>
      <div className={`text-xl font-bold ${highlight ? 'text-rose-700' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}

function GroupCard({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid={testid}>
      <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
      {entries.length === 0 ? (
        <div className="text-sm text-gray-400">لا يوجد بيانات</div>
      ) : (
        <ul className="space-y-1 text-sm">
          {entries.map(([key, val]) => (
            <li key={key} className="flex justify-between border-b border-gray-50 py-1">
              <span className="text-gray-700 truncate max-w-[65%]" title={key}>{key}</span>
              <span className="font-semibold text-gray-900 whitespace-nowrap">{fmt(val)} SAR</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
